import React, { useCallback } from "react";
import { useTranslations } from "@src/packages/i18n";
import { useBasicInfo } from "../BasicInfoContext";
import {
  BasicInfoQuestionLayout,
  DiagnosisStatusOptions,
  type DiagnosisStatus,
} from "../components";
import { useNavigation } from "@react-navigation/native";
import { usePatient } from "@src/packages/patients/hooks/usePatient";
import { useQueuedPatientUpdate } from "@src/packages/patients/sync/usePatientSync";

export function PCOSScreen() {
  const t = useTranslations();
  const { state, updateState, next } = useBasicInfo("PCOS");
  const navigation = useNavigation();
  const { data: patient } = usePatient();
  const { mutateAsync: updatePatient, isPending } = useQueuedPatientUpdate();

  // Determine current selection based on state
  const selection: DiagnosisStatus | null =
    state.pcosDiagnosed === true
      ? "diagnosed"
      : state.suspectPCOS === true
        ? "suspected"
        : state.suspectPCOS === false
          ? "no"
          : null;

  const handleSelection = useCallback(
    async (val: DiagnosisStatus) => {
      let stateUpdates: {
        suspectPCOS: boolean;
        pcosDiagnosed: boolean;
      };

      if (val === "diagnosed") {
        // Diagnosed: store in diseases table, not suspectPCOS
        stateUpdates = {
          suspectPCOS: false, // Not suspected, it's diagnosed
          pcosDiagnosed: true,
        };
        updateState(stateUpdates);
        try {
          await updatePatient({
            medicalHistory: {
              id: patient?.medicalHistory?.id,
              suspectPCOS: false,
              diseases: [
                ...(patient?.medicalHistory?.diseases?.filter(
                  (d) => d.disease !== "pcos",
                ) ?? []),
                { disease: "pcos" },
              ],
            },
          });
        } catch {
          return;
        }
      } else if (val === "suspected") {
        // Suspected/undiagnosed: store in suspectPCOS
        stateUpdates = {
          suspectPCOS: true,
          pcosDiagnosed: false,
        };
        updateState(stateUpdates);
        try {
          await updatePatient({
            medicalHistory: {
              id: patient?.medicalHistory?.id,
              suspectPCOS: true,
              diseases:
                patient?.medicalHistory?.diseases?.filter(
                  (d) => d.disease !== "pcos",
                ) ?? [],
            },
          });
        } catch {
          return;
        }
      } else {
        // No: clear both
        stateUpdates = {
          suspectPCOS: false,
          pcosDiagnosed: false,
        };
        updateState(stateUpdates);
        try {
          await updatePatient({
            medicalHistory: {
              id: patient?.medicalHistory?.id,
              suspectPCOS: false,
              diseases:
                patient?.medicalHistory?.diseases?.filter(
                  (d) => d.disease !== "pcos",
                ) ?? [],
            },
          });
        } catch {
          return;
        }
      }

      if (selection === null) {
        next(stateUpdates);
      }
    },
    [
      next,
      selection,
      updateState,
      updatePatient,
      patient?.medicalHistory?.id,
      patient?.medicalHistory?.diseases,
    ],
  );

  return (
    <BasicInfoQuestionLayout
      route="PCOS"
      title={t("med_history.basic_info.pcos.title")}
      helpText={t("med_history.basic_info.help.pcos")}
      onBack={() => navigation.goBack()}
      onSave={() => next()}
      saveDisabled={selection === null || isPending}
      hideSaveButton={selection === null}
      saveLabel={t("med_history.shared.next")}
    >
      <DiagnosisStatusOptions
        diagnosedLabel={t("med_history.shared.yes_diagnosed")}
        noLabel={t("med_history.shared.no")}
        onChange={handleSelection}
        suspectedLabel={t("med_history.shared.maybe_undiagnosed")}
        value={selection}
      />
    </BasicInfoQuestionLayout>
  );
}

export default PCOSScreen;
